import type { EventRecord } from '@polkadot/types/interfaces'
import type { Chain } from '../../chain'
import { type BlockData, type BlockEvent, blockEventSchema } from '../../types'

type GetBlockDatasParameters = {
  chain: Chain
  blocks: readonly {
    blockNumber: number
    extrinsicIndexes: (number | undefined)[]
  }[]
  timeout?: number
  onFetched?: (blockNumber: number) => void
  debug?: (message: unknown) => void
}

type GetBlockDatasReturnTypes = {
  blocks: readonly BlockData[]
  errors: Map<number, unknown>
}

export const getBlockDatas = async ({
  chain,
  blocks,
  timeout,
  onFetched,
  debug,
}: GetBlockDatasParameters): Promise<GetBlockDatasReturnTypes> => {
  const blockDatas: BlockData[] = []
  const errors = new Map<number, unknown>()

  // block events を並列取得
  await Promise.all(
    blocks.map(({ blockNumber, extrinsicIndexes }) =>
      (async () => {
        try {
          const events = await chain.client.call(async (api) => {
            const hash = await api.rpc.chain.getBlockHash(blockNumber)
            const apiDecoration = await api.at(hash)
            const events = await apiDecoration.query.system.events()
            return events as unknown as EventRecord[]
          }, timeout)

          const blockEvents = new Map<
            number | undefined,
            readonly BlockEvent[]
          >()
          for (const index of extrinsicIndexes) {
            const targetEvents = events
              .filter(({ phase }) =>
                index === undefined
                  ? !phase.isApplyExtrinsic
                  : phase.isApplyExtrinsic && phase.asApplyExtrinsic.eq(index),
              )
              .map((record) => record.event.toHuman())
            const parsedEvents: BlockEvent[] = []
            for (const event of targetEvents) {
              const parsed = blockEventSchema.safeParse(event)
              if (parsed.success) {
                parsedEvents.push(parsed.data)
                continue
              }
              if (debug !== undefined) {
                debug(`Failed to parse event: ${JSON.stringify(event)}`)
                debug(parsed.error.message)
              }
            }
            blockEvents.set(index, parsedEvents)
          }

          if (onFetched !== undefined) {
            onFetched(blockNumber)
          }
          blockDatas.push({
            blockNumber,
            events: blockEvents,
          })
        } catch (error) {
          errors.set(blockNumber, error)
        }
      })(),
    ),
  )

  return {
    blocks: blockDatas,
    errors,
  }
}
